import { useCallback, useEffect, useRef, useState } from "react";

import { useOnStoreChange } from "@/Common/CustomHooks/useOnStoreChange";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { PopulationBasedFundingHelper } from "@/Common/Helpers/PopulationBasedFundingHelper";
import { PatientRefIntervenorWithDetailsModel } from "@/Common/Models/Patient/PatientRefIntervenorWithDetailsModel";
import { PopulationBasedFundingStatusIcon } from "@/Common/Models/PopulationBasedFunding/PopulationBasedFundingStatusIcon";
import { PopulationBasedFundingActions } from "@/Common/Services/PopulationBasedFunding/PopulationBasedFundingService";

import style from "./PopulationBasedFundingStatusController.module.scss";

import Img_icon_not_registered from "@/Common/_images/PBF_NotRegistered.svg";
import Img_icon_not_registered_but_receive_treatment from "@/Common/_images/PBF_NotRegisteredButReceivesTreat.svg";
import Img_icon_pending_deregistration from "@/Common/_images/PBF_PendingDeregistration.svg";
import Img_icon_pending_registration from "@/Common/_images/PBF_PendingRegistration.svg";
import Img_icon_registered from "@/Common/_images/PBF_Registered.svg";
import Img_icon_registered_to_another_pbf_payee from "@/Common/_images/PBF_RegisteredToAnotherPbfPayee.svg";
import Img_icon_another_physician_within_same_reporting_group from "@/Common/_images/PBF_RegisteredToAnotherPbfPayeeWithinTheSameReportingGroup.svg";
import Img_icon_registered_to_different_physician from "@/Common/_images/PBF_RegisteredToDiffPhys.svg";

interface IPopulationBasedFundingStatusControllerProps {
    onClickOpenRegistrationPopupActionAsync?: () => Promise<void>;
    onClickRedirectToPbfTabAction?: () => void;
    patientContactFacetId: string;
    showPracticianName?: boolean;
    practician: PatientRefIntervenorWithDetailsModel;
    showCheckbox?: boolean;
}

function PopulationBasedFundingStatusController(props: IPopulationBasedFundingStatusControllerProps): JSX.Element {
    const [iconEnum, setIconEnum] = useState<PopulationBasedFundingStatusIcon>(PopulationBasedFundingStatusIcon.Unknown);
    const [shouldOpenRegistrationPopup, setShouldOpenRegistrationPopup] = useState(false);
    const { getText } = useTranslation();
    const version = useRef(1);
    const isInternalProposal = useRef(false);
    const [isSaving, setIsSaving] = useState(false);

    const textWhenNoStarredPhysician = iconEnum == PopulationBasedFundingStatusIcon.RegisteredToDifferentPhysician
        ? getText("PopulationBasedFunding_RegisteredStatus")
        : getText("PopulationBasedFunding_NotRegisteredStatus");

    const onInternalProposalCheckboxChange = (): void => {
        setIsSaving(true);
    };

    const getStatusIcon = (): JSX.Element => {
        switch (iconEnum) {
            case PopulationBasedFundingStatusIcon.NotRegistered:
                return <Img_icon_not_registered />;
            case PopulationBasedFundingStatusIcon.NotRegisteredButReceiveTreatment:
                return <Img_icon_not_registered_but_receive_treatment />;
            case PopulationBasedFundingStatusIcon.Registered:
                return <Img_icon_registered />;
            case PopulationBasedFundingStatusIcon.RegisteredToDifferentPhysician:
                return <Img_icon_registered_to_different_physician />;
            case PopulationBasedFundingStatusIcon.RegisteredToAnotherPbfPayee:
                return <Img_icon_registered_to_another_pbf_payee />;
            case PopulationBasedFundingStatusIcon.PendingRegistration:
                return <Img_icon_pending_registration />;
            case PopulationBasedFundingStatusIcon.PendingDeregistration:
                return <Img_icon_pending_deregistration />;
            case PopulationBasedFundingStatusIcon.RegisteredToAnotherPbfPayeeWithinTheSameReportingGroup:
                return <Img_icon_another_physician_within_same_reporting_group />;
            default:
                return <></>;
        }
    };

    const loadStatus = useCallback(
        async () => {
            const latestEvent = await PopulationBasedFundingActions.getLatestActiveEvent(props.patientContactFacetId);
            isInternalProposal.current = latestEvent?.IsInternalProposal ?? false;

            const icon = PopulationBasedFundingHelper.determineStatusIcon(
                latestEvent?.ActionEnum,
                latestEvent?.WillContinueTreatment,
                latestEvent?.isPendingOrOverriddenProposal,
                latestEvent?.BatchEligibilityCheckStatusEnum,
                latestEvent.EmployeeContactFacetId === props.practician?.Employee?.ContactFacetId
            );

            setIconEnum(icon);
            setShouldOpenRegistrationPopup(!latestEvent.isRegistered && !latestEvent.isProposal);

            version.current = patientStore.PopulationBasedFundingUpdateIconSignal.version;
        }
        , [props.patientContactFacetId, props.practician, isInternalProposal]
    );

    useOnStoreChange(patientStore, () => {
        if (
            (
                patientStore.PopulationBasedFundingUpdateIconSignal.patientContactFacetId == props.patientContactFacetId
                || patientStore.PopulationBasedFundingUpdateIconSignal.patientContactFacetId == null
            )
            && patientStore.PopulationBasedFundingUpdateIconSignal.version != version.current
        ) {
            loadStatus();
        }
    });

    useEffect(() => {
        loadStatus();
    }, [props.patientContactFacetId, props.practician?.Employee?.ContactFacetId, loadStatus]);

    useEffect(
        () => {
            const execute = async (): Promise<void> => {
                if (isSaving) {
                    const responseIsSuccesfull = !isInternalProposal.current
                        ? await PopulationBasedFundingActions.createInternalRegistrationProposal(props.patientContactFacetId)
                        : await PopulationBasedFundingActions.deleteInternalRegistrationProposal(props.patientContactFacetId);

                    if (responseIsSuccesfull)
                        isInternalProposal.current = !isInternalProposal.current;

                    setIsSaving(false);
                }
            };

            execute();
        }
        , [isSaving, props.patientContactFacetId]
    );

    return (
        <div data-cy="population-based-funding-icon">
            <div className={style.pbfStatus} onClick={async () => shouldOpenRegistrationPopup ? await props.onClickOpenRegistrationPopupActionAsync?.() : props.onClickRedirectToPbfTabAction?.()}>
                {getStatusIcon()}
                {props.showPracticianName &&
                    props.practician
                    ? props.practician.GetName()
                    : textWhenNoStarredPhysician
                }
            </div>

            {props.showCheckbox
                && (iconEnum === PopulationBasedFundingStatusIcon.NotRegisteredButReceiveTreatment
                    || iconEnum === PopulationBasedFundingStatusIcon.NotRegistered)
                && (
                    <div className={style.pbfStatusCheckbox}>
                        <input
                            type="checkbox"
                            id="register-patient"
                            checked={isInternalProposal.current}
                            onChange={() => onInternalProposalCheckboxChange()}
                            disabled={isSaving}
                        />
                        <label htmlFor="register-patient">{getText("AddPatientToPBFLabel")}</label>
                    </div>
                )
            }
        </div>
    );
}

export { PopulationBasedFundingStatusController };
